/**
 * @file Token validation helpers.
 * @description Shared guards and mappers for token-expiration/invalid-token API errors.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import { ApiResponse, TokenExpiredError } from '../types';

/**
 * Checks if an API response indicates token expiration or invalidity
 */
export const isTokenExpired = (response: ApiResponse): boolean => {
  return (
    response.status === 'error' &&
    (response.message.toLowerCase().includes('invalid') ||
     response.message.toLowerCase().includes('expired') ||
     response.message.toLowerCase().includes('token'))
  );
};

/**
 * Creates a token expired error
 */
export const createTokenExpiredError = (message: string): TokenExpiredError => {
  return {
    message,
    code: 'TOKEN_EXPIRED',
    requiresLogout: true,
  };
};

/**
 * Checks if an error is a token-related error that requires logout
 */
export const isTokenError = (error: unknown): error is TokenExpiredError => {
  if (!error || typeof error !== 'object') return false;
  const tokenError = error as Partial<TokenExpiredError>;
  return (
    (tokenError.code === 'TOKEN_EXPIRED' || tokenError.code === 'INVALID_TOKEN') &&
    tokenError.requiresLogout === true
  );
};

/**
 * Extracts token expiration message from various error formats
 */
export const getTokenErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && typeof error === 'object') {
    const maybeError = error as {
      message?: unknown;
      response?: { message?: unknown };
    };
    if (typeof maybeError.message === 'string' && maybeError.message.trim() !== '') {
      return maybeError.message;
    }
    
    if (
      maybeError.response &&
      typeof maybeError.response.message === 'string' &&
      maybeError.response.message.trim() !== ''
    ) {
      return maybeError.response.message;
    }
  }
  
  return 'Your session has expired. Please log in again.';
};
